
import { TutorialStep, TutorialContext } from '../contracts/scoring';

export const tutorialSteps = (lang: 'en' | 'fr', context: TutorialContext): TutorialStep[] => {
  const isFr = lang === 'fr';

  if (context === 'RESULTS') {
    return [
      {
        targetId: 'result-main-score',
        title: isFr ? "Score de Fidélité" : "Fidelity Score",
        content: isFr 
          ? "C'est votre score global. Il synthétise votre calage rythmique, votre justesse harmonique et la qualité de votre toucher."
          : "This is your overall score. It summarizes your rhythmic timing, harmonic pitch, and touch quality.",
        position: 'bottom'
      },
      {
        targetId: 'result-radar-chart',
        title: isFr ? "Tonal Fingerprint" : "Tonal Fingerprint",
        content: isFr 
          ? "Ce radar montre vos points techniques en un coup d'œil. Plus la zone est large, plus vous approchez du jeu 'Master'."
          : "This radar shows your technical strengths at a glance. The wider the area, the closer you are to the 'Master' playing style.",
        position: 'bottom'
      },
      {
        targetId: 'result-tabs-nav',
        title: isFr ? "Analyses Détaillées" : "Detailed Analytics",
        content: isFr 
          ? "Naviguez ici pour voir les preuves scientifiques : Sync Match (rythme), Vision Lab (posture) ou l'Analyse d'Attaque."
          : "Navigate here to see scientific evidence: Sync Match (rhythm), Vision Lab (posture), or Attack Analysis.",
        position: 'bottom'
      },
      {
        targetId: 'result-action-plan',
        title: isFr ? "Plan d'Action IA" : "AI Action Plan",
        content: isFr 
          ? "Le Coach IA a analysé vos erreurs prioritaires et vous propose des exercices (Drills) spécifiques pour votre prochaine session."
          : "The AI Coach has analyzed your priority errors and suggests specific drills for your next session.",
        position: 'top'
      }
    ];
  }

  if (context === 'RECORDER') {
    return [
      {
        targetId: 'studio-console-btn',
        title: isFr ? "Le Panneau de Console" : "Studio Console",
        content: isFr 
          ? "C'est ici que vous réglez tout : votre tempo, le volume du métronome et votre gain micro. Gardez ce menu ouvert pour ajuster votre son."
          : "This is where you set everything: your tempo, metronome volume, and mic gain. Keep this menu open to adjust your sound.",
        position: 'right'
      },
      {
        targetId: 'studio-view-switcher',
        title: isFr ? "Gestion des Vues" : "View Switcher",
        content: isFr 
          ? "Choisissez comment vous voulez vous entraîner : vous seul, le professeur seul, ou les deux en split-screen 50/50."
          : "Choose how you want to practice: just you, just the teacher, or both in a 50/50 split-screen.",
        position: 'bottom'
      },
      {
        targetId: 'studio-master-speed',
        title: isFr ? "Répétition au ralenti" : "Slow Motion Practice",
        content: isFr 
          ? "Un passage difficile ? Ralentissez la vidéo du professeur pour décortiquer ses mouvements sans changer le pitch."
          : "Tough passage? Slow down the teacher's video to analyze their movements without changing the pitch.",
        position: 'bottom'
      },
      {
        targetId: 'studio-vu-meter',
        title: isFr ? "Le VU-Mètre" : "Input VU-Meter",
        content: isFr 
          ? "Règle de base : assurez-vous que les barres s'allument quand vous jouez. Si c'est rouge, baissez le gain IA pour éviter de saturer."
          : "Basic rule: make sure the bars light up when you play. If it's red, lower the AI gain to avoid clipping.",
        position: 'bottom'
      },
      {
        targetId: 'studio-calib-btn',
        title: isFr ? "La Calibration (Crucial)" : "Latency Calibration",
        content: isFr 
          ? "Ne soyez pas intimidé ! C'est un test de 3 secondes qui mesure le retard de votre micro. L'IA en a besoin pour être parfaitement juste dans ses scores."
          : "Don't be intimidated! It's a 3-second test that measures your mic lag. The AI needs this to be perfectly accurate with your scores.",
        position: 'top'
      },
      {
        targetId: 'studio-rec-btn',
        title: isFr ? "Le Bouton REC" : "The REC Button",
        content: isFr 
          ? "Prêt ? Cliquez ici. Vous entendrez 4 clics de décompte pour vous caler avant que l'IA ne commence l'analyse de votre performance."
          : "Ready? Click here. You'll hear 4 countdown clicks to lock in before the AI starts analyzing your performance.",
        position: 'top'
      }
    ];
  }

  return [
    {
      targetId: 'upload-zones',
      title: isFr ? "Le Cœur du Studio" : "The Studio Core",
      content: isFr 
        ? "C'est ici que tout se joue. À gauche, la cible Master (le modèle à suivre). À droite, votre espace pour enregistrer votre performance via webcam ou micro."
        : "This is where the magic happens. On the left, the Master target (your model). On the right, your space to record your performance via webcam or mic.",
      position: 'top'
    },
    {
      targetId: 'tempo-zone',
      title: isFr ? "Le Moteur Rythmique" : "The Rhythmic Engine",
      content: isFr 
        ? "Réglez le tempo du Master ici. L'IA utilisera cette valeur pour mesurer votre précision temporelle à la milliseconde près."
        : "Set the Master tempo here. The AI will use this value to measure your temporal precision down to the millisecond.",
      position: 'top'
    },
    {
      targetId: 'grid-zone',
      title: isFr ? "La Structure Harmonique" : "The Harmonic Map",
      content: isFr 
        ? "Indiquez les accords du morceau. L'IA Gemini les utilisera pour valider votre justesse et votre placement sur le manche."
        : "Enter the chords of the song. The Gemini AI will use them to validate your pitch and fretboard placement.",
      position: 'bottom'
    },
    {
      targetId: 'nav-bar',
      title: isFr ? "Sources de Contenu" : "Content Sources",
      content: isFr 
        ? "Besoin d'un Master ? Explorez le Songbook interactif ou importez directement n'importe quel riff depuis YouTube."
        : "Need a Master? Explore the interactive Songbook or import any riff directly from YouTube.",
      position: 'bottom'
    },
    {
        targetId: 'btn-menu',
        title: isFr ? "Menu & Profil" : "Menu & Profile",
        content: isFr 
            ? "Accédez à votre historique, vos récompenses Arena et les réglages avancés de sévérité du Coach." 
            : "Access your history, Arena rewards, and the Coach's advanced severity settings.",
        position: 'bottom'
    }
  ];
};
